/**
 * logger.js - Centralized logging utility
 * Provides consistent logging across the application
 */

// Log levels with emoji indicators
const LOG_LEVELS = {
    CLICK: '🖱️',
    API: '📡',
    ERROR: '❌',
    INFO: 'ℹ️',
    WARNING: '⚠️',
    SUCCESS: '✅'
};

// Check if logging is enabled
const isLoggingEnabled = () => {
    // Enable in development or if explicitly enabled
    return process.env.NODE_ENV === 'development' ||
        process.env.REACT_APP_ENABLE_LOGGING === 'true';
};

/**
 * Log a click event
 * @param {HTMLElement} target - The clicked element
 * @param {Object} context - Additional context
 */
export function logClick(target, context = {}) {
    if (!isLoggingEnabled()) return;

    const element = target.tagName?.toLowerCase() || 'unknown';
    const text = (target.textContent || target.value || '').trim().slice(0, 50);
    const id = target.id;
    const className = target.className;
    const role = target.getAttribute('role');

    console.log(`${LOG_LEVELS.CLICK} CLICK [${element}]:`, {
        element,
        id: id || 'no-id',
        className: className || 'no-class',
        role: role || 'no-role',
        text: text || 'no-text',
        timestamp: new Date().toISOString(),
        ...context
    });
}

/**
 * Log an API call
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} response - Response data
 * @param {number} duration - Request duration in ms
 */
export function logAPI(method, url, data = null, response = null, duration = null) {
    if (!isLoggingEnabled()) return;

    const logData = {
        method: method.toUpperCase(),
        url,
        timestamp: new Date().toISOString()
    };

    if (data) {
        // Sanitize sensitive fields
        logData.data = sanitizeData(data);
    }

    if (response) {
        logData.status = response.status;
        logData.responseData = sanitizeData(response.data);
    }

    if (duration !== null) {
        logData.duration = `${duration}ms`;
    }

    console.log(`${LOG_LEVELS.API} API ${method.toUpperCase()}:`, logData);
}

/**
 * Log an error
 * @param {string} message - Error message
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export function logError(message, error = null, context = {}) {
    if (!isLoggingEnabled()) return;

    console.error(`${LOG_LEVELS.ERROR} ERROR:`, {
        message,
        error: error?.message || error,
        stack: error?.stack,
        timestamp: new Date().toISOString(),
        ...context
    });
}

/**
 * Log general info
 * @param {string} message - Info message
 * @param {Object} data - Additional data
 */
export function logInfo(message, data = {}) {
    if (!isLoggingEnabled()) return;

    console.log(`${LOG_LEVELS.INFO} INFO:`, {
        message,
        timestamp: new Date().toISOString(),
        ...data
    });
}

/**
 * Log a warning
 * @param {string} message - Warning message
 * @param {Object} data - Additional data
 */
export function logWarning(message, data = {}) {
    if (!isLoggingEnabled()) return;

    console.warn(`${LOG_LEVELS.WARNING} WARNING:`, {
        message,
        timestamp: new Date().toISOString(),
        ...data
    });
}

/**
 * Log a success event
 * @param {string} message - Success message
 * @param {Object} data - Additional data
 */
export function logSuccess(message, data = {}) {
    if (!isLoggingEnabled()) return;

    console.log(`${LOG_LEVELS.SUCCESS} SUCCESS:`, {
        message,
        timestamp: new Date().toISOString(),
        ...data
    });
}

/**
 * Sanitize sensitive data
 * @param {Object} data - Data to sanitize
 * @returns {Object} Sanitized data
 */
function sanitizeData(data) {
    if (!data || typeof data !== 'object') return data;

    const sensitiveKeys = [
        'password',
        'token',
        'secret',
        'apiKey',
        'api_key',
        'accessToken',
        'refreshToken',
        'credit_card',
        'creditCard',
        'cvv',
        'ssn'
    ];

    const sanitized = Array.isArray(data) ? [...data] : { ...data };

    for (const key in sanitized) {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
            sanitized[key] = '***REDACTED***';
        } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
            sanitized[key] = sanitizeData(sanitized[key]);
        }
    }

    return sanitized;
}

export default {
    logClick,
    logAPI,
    logError,
    logInfo,
    logWarning,
    logSuccess
};
